import { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Inbox, Loader2, Mail } from "lucide-react";
import { api, fmtDate, apiError } from "@/lib/api";

const STATUS_META = {
  new: { label: "New", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  contacted: { label: "Contacted", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  resolved: { label: "Resolved", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
};

export default function AdminSupportLeads() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [promiseBusy, setPromiseBusy] = useState(false);
  const [alertEmail, setAlertEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  const load = useCallback((status = filter) => {
    api.get("/admin/support/leads", { params: status ? { status } : {} })
      .then(({ data }) => { setData(data); setAlertEmail(data.alert_email || ""); })
      .catch((e) => toast.error(apiError(e, "Couldn't load support leads")));
  }, [filter]);
  useEffect(() => { load(filter); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (lead, status) => {
    if (busyId) return;
    setBusyId(lead.id);
    try {
      await api.patch(`/admin/support/leads/${lead.id}`, { status });
      toast.success(`Marked ${STATUS_META[status].label.toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't update the lead"));
    } finally {
      setBusyId(null);
    }
  };

  const togglePromise = async () => {
    if (promiseBusy || !data) return;
    setPromiseBusy(true);
    try {
      const next = !data.promise_enabled;
      await api.put("/admin/support/leads-config", { promise_enabled: next });
      setData((d) => ({ ...d, promise_enabled: next }));
      toast.success(next
        ? "Customers will now be told the team will reach out"
        : "Follow-up promise disabled — confirmation stays neutral");
    } catch (e) {
      toast.error(apiError(e, "Couldn't save the setting"));
    } finally {
      setPromiseBusy(false);
    }
  };

  const saveAlertEmail = async () => {
    if (emailBusy || !data) return;
    setEmailBusy(true);
    try {
      await api.put("/admin/support/leads-config", { promise_enabled: !!data.promise_enabled, alert_email: alertEmail.trim() });
      toast.success(`Lead alerts will go to ${alertEmail.trim()}`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't save the alert email"));
    } finally {
      setEmailBusy(false);
    }
  };

  const counts = data?.counts || { new: 0, contacted: 0, resolved: 0 };
  const leads = data?.leads || [];

  return (
    <div data-testid="leads-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-white"><Inbox className="h-6 w-6 text-amber-400" /> Support Leads</h1>
          <p className="mt-1 text-sm text-neutral-500">Customers who asked the chatbot something it couldn't answer.</p>
        </div>
        <button onClick={togglePromise} disabled={promiseBusy || !data} data-testid="leads-promise-toggle"
          className={`flex items-center gap-2.5 rounded-xl border px-4 py-2.5 text-xs font-semibold transition-colors ${
            data?.promise_enabled
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              : "border-white/10 bg-white/[0.03] text-neutral-400 hover:text-white"
          }`}>
          {promiseBusy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Follow-up promise: {data?.promise_enabled ? "ON — customers are told the team will reach out" : "OFF — neutral confirmation"}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/[0.07] bg-white/[0.02] px-4 py-3">
        <Mail className="h-4 w-4 text-amber-400" />
        <span className="text-xs font-semibold text-neutral-400">New-lead alerts go to</span>
        <input value={alertEmail} onChange={(e) => setAlertEmail(e.target.value)} type="email" data-testid="leads-alert-email"
          className="h-9 w-64 rounded-lg border border-white/10 bg-black/40 px-3 text-xs text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none" />
        <button onClick={saveAlertEmail} disabled={emailBusy || !alertEmail.trim()} data-testid="leads-alert-email-save"
          className="flex h-9 items-center gap-1.5 rounded-lg bg-amber-500 px-3.5 text-xs font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-40">
          {emailBusy && <Loader2 className="h-3 w-3 animate-spin" />} Save
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {[["", "All"], ["new", `New (${counts.new})`], ["contacted", `Contacted (${counts.contacted})`], ["resolved", `Resolved (${counts.resolved})`]].map(([v, label]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`leads-filter-${v || "all"}`}
            className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
              filter === v ? "border-amber-500/50 bg-amber-500/15 text-amber-300" : "border-white/10 text-neutral-400 hover:text-white"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
      ) : leads.length === 0 ? (
        <div data-testid="leads-empty" className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-16 text-center text-sm text-neutral-500">
          No support leads{filter ? ` marked ${STATUS_META[filter].label.toLowerCase()}` : ""} yet.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/[0.07]">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/[0.07] bg-white/[0.03] text-[11px] uppercase tracking-wider text-neutral-500">
                <th className="w-8 px-3 py-3"></th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Message</th>
                <th className="px-4 py-3">Received</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <Fragment key={lead.id}>
                  <tr data-testid={`lead-row-${lead.id}`} className="border-b border-white/[0.05] align-top hover:bg-white/[0.02]">
                    <td className="px-3 py-3">
                      <button onClick={() => setExpanded(expanded === lead.id ? null : lead.id)} data-testid={`lead-expand-${lead.id}`}
                        aria-label="Show chat context" className="text-neutral-500 hover:text-amber-300">
                        {expanded === lead.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-white">{lead.name}</p>
                      <a href={`mailto:${lead.email}`} className="text-xs text-amber-300/80 hover:text-amber-300">{lead.email}</a>
                    </td>
                    <td className="max-w-md px-4 py-3 text-neutral-300">
                      <p className="line-clamp-2 whitespace-pre-wrap">{lead.message}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-neutral-500">{fmtDate(lead.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(STATUS_META).map(([s, meta]) => (
                          <button key={s} onClick={() => lead.status !== s && setStatus(lead, s)} disabled={busyId === lead.id}
                            data-testid={`lead-status-${lead.id}-${s}`}
                            className={`rounded-full border px-2.5 py-1 text-[11px] font-bold transition-opacity ${
                              lead.status === s ? meta.cls : "border-white/10 text-neutral-600 hover:text-neutral-300"
                            } ${busyId === lead.id ? "opacity-40" : ""}`}>
                            {meta.label}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                  {expanded === lead.id && (
                    <tr data-testid={`lead-context-${lead.id}`} className="border-b border-white/[0.05] bg-black/30">
                      <td colSpan={5} className="px-6 py-4">
                        <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-neutral-500">Chat context</p>
                        {lead.context?.length ? (
                          <div className="space-y-1.5">
                            {lead.context.map((m, i) => (
                              <p key={i} className="text-xs leading-relaxed text-neutral-400">
                                <span className={`font-bold ${m.role === "user" ? "text-amber-300/80" : "text-neutral-300"}`}>
                                  {m.role === "user" ? "Customer" : "Bot"}:
                                </span>{" "}{m.text}
                              </p>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-neutral-600">No chat context was attached to this lead.</p>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
